import { html, nothing, unsafeCSS, type CSSResultGroup } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { LibElement } from '../lib-element';
import '../icon/lib-icon';
import buttonCss from '../button/lib-button.css';
import css from './lib-pagination.css';

/** Page, slide and step navigation with one-based selection. */
@customElement('lib-pagination')
export class LibPagination extends LibElement {
  protected override get hostFocusSelector(): string { return 'button'; }
  static override readonly styles: CSSResultGroup = [LibElement.hostStyles, unsafeCSS(buttonCss), unsafeCSS(css)];

  @property() variant = 'long';
  @property({ type: Number, attribute: 'page-count' }) pageCount = 1;
  @property({ type: Number, attribute: 'current-page' }) currentPage = 1;
  @property({ type: Boolean, reflect: true }) disabled = false;
  @property() text: string | null = null;
  @property({ attribute: 'aria-label' }) override ariaLabel = 'Pagination';
  @property({ attribute: 'previous-label' }) previousLabel = 'Previous page';
  @property({ attribute: 'next-label' }) nextLabel = 'Next page';
  @property({ attribute: 'page-label' }) pageLabel = 'Page';
  @property({ attribute: 'of-label' }) ofLabel = 'of';

  get value(): string { return String(this.currentPage); }
  get #count(): number { return Math.max(1, Math.trunc(this.pageCount) || 1); }
  get #isList(): boolean { return this.variant === 'long' || this.variant === 'short'; }

  protected override willUpdate(): void {
    this.currentPage = Math.min(this.#count, Math.max(1, Math.trunc(this.currentPage) || 1));
  }

  #items(): (number | null)[] {
    const slots = this.variant === 'short' ? 5 : 7;
    const range = (first: number, last: number) => Array.from({ length: last - first + 1 }, (_, i) => first + i);
    if (this.#count <= slots) return range(1, this.#count);
    const block = slots - 2;
    if (this.currentPage <= slots - 3) return [...range(1, block), null, this.#count];
    if (this.currentPage >= this.#count - (slots - 4)) return [1, null, ...range(this.#count - block + 1, this.#count)];
    const neighbours = (slots - 5) / 2;
    return [1, null, ...range(this.currentPage - neighbours, this.currentPage + neighbours), null, this.#count];
  }

  #arrow(previous: boolean) {
    const label = previous ? this.previousLabel : this.nextLabel;
    const page = this.currentPage + (previous ? -1 : 1);
    const icon = previous ? 'chevron-left' : 'chevron-right';
    return html`<button type="button" class="lib-button lib-button-${this.#isList ? 'tertiary' : 'secondary'}"
      aria-label=${label} title=${label} ?disabled=${this.disabled || page < 1 || page > this.#count}
      @click=${() => this.#select(page)}>
      <lib-icon icon-url=${new URL(`../wago-icons-svg/${icon}.svg`, import.meta.url).href}></lib-icon>
    </button>`;
  }

  override render() {
    const pageText = this.text ?? `${this.currentPage} ${this.ofLabel} ${this.#count}`;
    return html`<nav class="lib-pagination lib-pagination-${this.variant}" aria-label=${this.ariaLabel}>
      ${this.#arrow(true)}
      ${this.#isList ? html`<ol class="lib-pagination-list">${this.#items().map(page => this.#renderItem(page))}
        </ol>` : html`<span class="lib-pagination-text" aria-live="polite">${pageText}</span>`}
      ${this.#arrow(false)}
    </nav>`;
  }

  #select(page: number): void {
    page = Math.min(this.#count, Math.max(1, page));
    if (this.disabled || page === this.currentPage) return;
    this.currentPage = page;
    this.dispatchEvent(new Event('change', { bubbles: true, composed: true }));
  }

  #renderItem(page: number | null) {
    if (page === null) return html`<li class="lib-pagination-dots" aria-hidden="true">...</li>`;
    const active = page === this.currentPage;
    const itemClass = active ? 'lib-pagination-item-active' : 'lib-button-regular';
    const label = `${this.pageLabel} ${page}`;
    return html`<li><button type="button"
      class="lib-button lib-button-tertiary lib-pagination-item ${itemClass}"
      aria-label=${label} aria-current=${active ? 'page' : nothing}
      ?disabled=${this.disabled} @click=${() => this.#select(page)}>${page}</button></li>`;
  }
}

declare global {
  interface HTMLElementTagNameMap { 'lib-pagination': LibPagination; }
}
